import React, { useState, useEffect } from 'react';
import { Search, X } from 'lucide-react';
import axios from 'axios';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SearchBar({ onBoothSelect }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState({ booths: [], companies: [] });
  const [isSearching, setIsSearching] = useState(false);
  const [showResults, setShowResults] = useState(false);

  useEffect(() => {
    if (query.length < 2) {
      setResults({ booths: [], companies: [] });
      setShowResults(false);
      return;
    }

    const timer = setTimeout(async () => {
      setIsSearching(true);
      try {
        const response = await axios.get(`${API_URL}/search?q=${encodeURIComponent(query)}`);
        setResults(response.data.data);
        setShowResults(true);
      } catch (error) {
        console.error('Search error:', error);
      } finally {
        setIsSearching(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [query]);

  const handleBoothClick = (booth) => {
    onBoothSelect(booth);
    setQuery('');
    setShowResults(false);
  };

  return (
    <div className="relative w-full max-w-2xl mx-auto" data-testid="search-container">
      <div className="relative">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search booths, companies, categories..."
          className="w-full pl-12 pr-12 py-3.5 rounded-2xl bg-slate-900/70 backdrop-blur-xl border border-slate-700/50 text-white placeholder-slate-500 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all shadow-lg"
          data-testid="search-input"
        />
        {query && (
          <button
            onClick={() => {
              setQuery('');
              setResults({ booths: [], companies: [] });
              setShowResults(false);
            }}
            className="absolute right-4 top-1/2 -translate-y-1/2 p-1 rounded-full hover:bg-slate-800"
            data-testid="clear-search-btn"
          >
            <X className="w-4 h-4 text-slate-400" />
          </button>
        )}
      </div>

      {/* Results dropdown */}
      {showResults && (results.booths.length > 0 || results.companies.length > 0) && (
        <div className="absolute top-full left-0 right-0 mt-2 bg-slate-900/95 backdrop-blur-xl border border-slate-700/50 rounded-2xl shadow-2xl overflow-hidden max-h-96 overflow-y-auto z-50" data-testid="search-results">
          {results.booths.length > 0 && (
            <div className="p-3">
              <div className="text-xs font-semibold text-slate-400 uppercase mb-2 px-2">Booths</div>
              {results.booths.map((booth) => (
                <button
                  key={booth.id}
                  onClick={() => handleBoothClick(booth)}
                  className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-slate-800/50 transition-colors text-left"
                  data-testid={`search-result-booth-${booth.id}`}
                >
                  <div 
                    className="w-10 h-10 rounded-lg flex items-center justify-center text-white font-bold text-xs"
                    style={{ backgroundColor: booth.color || '#3498db' }}
                  >
                    {booth.booth_number.slice(-3)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-white font-medium truncate">
                      {booth.booth_number} - {booth.company?.name || 'Available'}
                    </div>
                    <div className="text-xs text-slate-400 truncate">
                      {booth.hall?.name} • {booth.category?.name}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
